import type {BrandKitUpdateServerResponse} from '@Components/auto-branding/auto-branding.types';
import {StyleType} from '@Libraries/ga-events';
import {canUserAccessBrandKits} from '@Libraries/brands-library';
import type {Component, Editor} from 'grapesjs';
import {PALETTE_APPLIED_EVENT, PALETTE_CUSTOM_STYLE} from '@/types/email-maker-global.constants';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {ColorPaletteType, getAdminColorPalettesFromRedux, isSameColorPalette} from '@Libraries/color-palettes.library';

export const removeDuplicateColorPalettes = (colorPalettes: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  const uniqueColorPalettes: ColorPaletteInfo[] = [];

  colorPalettes.forEach((colorPalette): void => {
    if (
      !uniqueColorPalettes.some((uniqueColorPalette): boolean => {
        return isSameColorPalette(colorPalette, uniqueColorPalette);
      })
    ) {
      uniqueColorPalettes.push(colorPalette);
    }
  });

  return uniqueColorPalettes;
};

export interface ColorPaletteInfoToStore {
  colors: string[];
  customizedFromId?: string | number;
  customizedFromType?: ColorPaletteType;
  id?: number;
  name?: string;
}

const createPaletteInfoToStore = (colorPalette: ColorPaletteInfo): string => {
  const infoToStore: ColorPaletteInfoToStore = {
    colors: colorPalette.colors,
  };

  if (colorPalette.type === ColorPaletteType.Recent && colorPalette.id) {
    infoToStore.id = parseInt(colorPalette.id, 10);
    infoToStore.name = colorPalette.name;
  } else {
    infoToStore.customizedFromId = colorPalette.id;
    infoToStore.customizedFromType = colorPalette.type;
    infoToStore.name = colorPalette.name;
  }

  return JSON.stringify(infoToStore);
};

export const storeAppliedPaletteInHtml = (editor: Editor, colorPalette: ColorPaletteInfo): void => {
  const mainWrapper = editor.runCommand(window.PMW.emails.commands.GET_MAIN_WRAPPER) as Component;
  const infoToStore = createPaletteInfoToStore(colorPalette);

  mainWrapper.addStyle({[PALETTE_CUSTOM_STYLE]: infoToStore});
  window.dispatchEvent(new CustomEvent(PALETTE_APPLIED_EVENT));
};

export const updateBrandKitFromColorPalette = async (colorPalette: ColorPaletteInfo): Promise<boolean> => {
  if (!window.PMW.isUserLoggedIn() || !canUserAccessBrandKits()) {
    return false;
  }

  if (!colorPalette.id || colorPalette.colors.length === 0) {
    return false;
  }

  const data = (await window.PMW.writeLocal('autobrandingemailmaker/updateBrandKitFromColorPalette', {
    brandId: colorPalette.id,
    colors: colorPalette.colors,
  })) as BrandKitUpdateServerResponse;
  return data.updateSuccess;
};

export const filterColorPalettesBySearchTerm = (searchTerm: string, colorPalettesToSearchFrom: ColorPaletteInfo[]): ColorPaletteInfo[] => {
  if (colorPalettesToSearchFrom.length === 0) {
    return [];
  }

  const matchesPaletteTag = (palette: ColorPaletteInfo, tag: string): boolean => {
    return (
      palette.tags?.some((paletteTag) => {
        return paletteTag.name.toLowerCase().includes(tag.toLowerCase());
      }) ?? false
    );
  };
  const matchesPaletteName = (palette: ColorPaletteInfo, name: string): boolean => {
    return palette.name?.toLowerCase().includes(name.toLowerCase()) ?? false;
  };

  const filteredColorPalettes = colorPalettesToSearchFrom.filter((palette): boolean => {
    return matchesPaletteName(palette, searchTerm) || matchesPaletteTag(palette, searchTerm);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getColorPalettesByTagIds = (tagIds: number[]): ColorPaletteInfo[] => {
  const colorPalettes = getAdminColorPalettesFromRedux();

  if (!colorPalettes) {
    return [];
  }

  const paletteContainsAllOfTags = (palette: ColorPaletteInfo, ids: number[]): boolean => {
    const paletteTagIds = palette.tags?.map((tag) => tag.id) ?? [];
    return ids.every((id) => paletteTagIds.includes(id));
  };

  const filteredColorPalettes = colorPalettes.filter((palette): boolean => {
    return paletteContainsAllOfTags(palette, tagIds);
  });

  return filteredColorPalettes.map((colorPalette): ColorPaletteInfo => {
    return {
      id: colorPalette.id,
      colors: colorPalette.colors,
      tags: colorPalette.tags,
      name: colorPalette.name,
      type: ColorPaletteType.Admin,
    };
  });
};

export const getGA4StyleTypeFromColorPalette = (colorPalette: ColorPaletteInfo): string => {
  switch (colorPalette.type) {
    case ColorPaletteType.Admin:
      return StyleType.ALL_STYLES;
    case ColorPaletteType.BrandKit:
      return StyleType.BRAND_KIT;
    case ColorPaletteType.Recent:
      return StyleType.RECENTS;
    default:
      console.error('Unknown color palette type', colorPalette);
      return 'Unknown';
  }
};
